import React from 'react';
import { Link } from 'react-router-dom';
import { usePermission } from '../../hooks/usePermission';

/**
 * The "Catalogue ↗" chip on every quotation line: the Master Catalogue rows this
 * line's item could be priced from, in a new tab, with the row that DID price it
 * highlighted (BUSINESS_RULES#quotation-line-catalogue-options).
 *
 * The same idea as the RFQ > Quote > Order chain rail, one level down, and drawn
 * the way that rail draws its links: a small labelled chip. The first version was
 * a bare chain-link icon squeezed between the search icon and the name; the owner
 * found it hard to read and chose this chip (2026-09-14). It sits AFTER the name,
 * so every name in the column still starts in the same place.
 *
 * A NEW TAB, by the owner's choice: a reviewer is usually halfway down a long
 * quotation and should not lose their place to look something up.
 *
 * Cyan when the line records a catalogue row and grey when it does not — a
 * Supplier dropdown pick or a typed price — so "was this priced from the
 * catalogue?" reads down the column without opening anything. Which row, and
 * whether it still exists, is the destination's job: the server resolves it
 * there, including rows merged or deleted since.
 */

/** Where the link goes. The line by number, because that is what the grid shows. */
export function catalogueOptionsHref(quotationId: string, lineNumber: number): string {
  const params = new URLSearchParams({ quotation: quotationId, line: String(lineNumber) });
  return `/master-catalogue?${params.toString()}`;
}

/** Whether the line records the catalogue row its current cost came from. */
export function recordsCatalogueRow(pricingDetails: unknown): boolean {
  return !!(
    pricingDetails &&
    typeof pricingDetails === 'object' &&
    (pricingDetails as Record<string, unknown>).masterCatalogueId
  );
}

// The chain rail's chip, at the grid's smaller type. Every state carries a
// border, so the chip is the same size whichever colour it is and the names
// beside it do not shift between lines.
const CHIP =
  'flex-shrink-0 inline-flex items-center gap-1 rounded-md border px-1.5 py-px ' +
  'text-xs font-medium leading-4 whitespace-nowrap transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600 focus-visible:ring-offset-1';

const CHIP_PRICED = `${CHIP} border-cyan-200 bg-cyan-50 text-cyan-800 hover:border-cyan-300 hover:bg-cyan-100`;
const CHIP_NOT_PRICED = `${CHIP} border-transparent bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700`;
const CHIP_INERT = `${CHIP} border-transparent bg-slate-50 text-slate-300 cursor-not-allowed`;

/** "Opens in a new tab", drawn rather than typed so it sits on the text's midline. */
const NewTabArrow = () => (
  <svg width="9" height="9" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.5 1.5h5v5M8.5 1.5l-7 7" />
  </svg>
);

export interface CatalogueOptionsLinkProps {
  quotationId: string;
  lineNumber: number;
  pricingDetails?: unknown;
}

export const CatalogueOptionsLink: React.FC<CatalogueOptionsLinkProps> = ({
  quotationId,
  lineNumber,
  pricingDetails,
}) => {
  const { canView } = usePermission('products');

  // A line with no number cannot be addressed, and a link to "line undefined"
  // would open on an error.
  if (!quotationId || !Number.isInteger(lineNumber) || lineNumber < 1) return null;

  // Degrades to an inert chip rather than a link into a 403 — the chain rail's
  // rule, for the same reason.
  if (!canView) {
    return (
      <span
        className={CHIP_INERT}
        title="You do not have access to the Master Catalogue"
        aria-label="Catalogue: no access to the Master Catalogue"
        data-testid="catalogue-options-link-disabled"
      >
        Catalogue
      </span>
    );
  }

  const priced = recordsCatalogueRow(pricingDetails);
  const title = priced
    ? `Open the Master Catalogue rows for this item in a new tab — the row that priced line ${lineNumber} is highlighted`
    : `Open the Master Catalogue rows for this item in a new tab. Line ${lineNumber} was not priced from a catalogue row, so none is highlighted`;

  return (
    <Link
      to={catalogueOptionsHref(quotationId, lineNumber)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={priced ? CHIP_PRICED : CHIP_NOT_PRICED}
      title={title}
      aria-label={`Catalogue rows for line ${lineNumber} (opens in a new tab)`}
      data-testid="catalogue-options-link"
      data-priced-from-row={priced ? 'true' : 'false'}
    >
      Catalogue
      <NewTabArrow />
    </Link>
  );
};

export default CatalogueOptionsLink;
